import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UtensilsCrossed, ChevronRight, Calendar, X, Phone } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { HelpPanel } from "../../components/customer/HelpButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

function SectionLabel({ children }) {
  return <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">{children}</p>;
}

export default function CustomerUpdates() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [lightbox, setLightbox] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/updates")
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load updates."), data: null }));
  };

  useEffect(() => { load(); }, []);

  if (state.loading) {
    return (
      <div data-testid="customer-updates-page" className="space-y-4">
        <Skeleton className="aspect-square w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-16 w-full rounded-2xl" />
      </div>
    );
  }

  if (state.error) return <ErrorState message={state.error} onRetry={load} testId="customer-updates-error" />;

  const menu = state.data?.menu;
  const other = state.data?.other;
  const menuImg = fullUrl(menu?.image_url);
  const otherImg = fullUrl(other?.image_url);

  return (
    <div className="space-y-5" data-testid="customer-updates-page">
      {/* Today's Menu */}
      <section data-testid="customer-updates-menu-section">
        <SectionLabel>Today's Menu</SectionLabel>
        {menuImg ? (
          <button
            type="button"
            onClick={() => setLightbox(menuImg)}
            className="block w-full overflow-hidden rounded-2xl border border-border bg-surface shadow-sm"
            data-testid="customer-updates-menu-image-button"
          >
            <img src={menuImg} alt="Today's menu" className="aspect-square w-full object-cover" data-testid="customer-updates-menu-image" />
            {menu?.comment && <p className="px-4 py-3 text-left text-sm font-medium text-text-main">{menu.comment}</p>}
          </button>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-surface p-6 text-center shadow-sm" data-testid="customer-updates-menu-empty">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <UtensilsCrossed size={22} strokeWidth={1.5} />
            </span>
            <p className="mt-3 text-sm font-semibold text-text-main">No update yet from The Virsa Tiffin</p>
            <p className="mt-1 text-xs text-text-muted">Today's menu will appear here once it's posted.</p>
            <a
              href="tel:"
              onClick={(e) => { e.preventDefault(); setHelpOpen(true); }}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary"
              data-testid="customer-updates-menu-contact"
            >
              <Phone size={12} strokeWidth={2} /> Contact us if you need any help
            </a>
          </div>
        )}
      </section>

      {/* Other Updates */}
      {other && (otherImg || other.body) && (
        <section data-testid="customer-updates-other-section">
          <SectionLabel>Latest Update</SectionLabel>
          <button
            type="button"
            onClick={() => otherImg && setLightbox(otherImg)}
            className="block w-full overflow-hidden rounded-2xl border border-border bg-surface text-left shadow-sm"
            data-testid="customer-updates-other-card"
          >
            {otherImg && <img src={otherImg} alt={other.title || "Update"} className="aspect-[4/3] w-full object-cover" data-testid="customer-updates-other-image" />}
            {(other.title || other.body) && (
              <div className="px-4 py-3">
                {other.title && <p className="text-sm font-semibold text-text-main">{other.title}</p>}
                {other.body && <p className="mt-0.5 text-xs text-text-muted">{other.body}</p>}
              </div>
            )}
          </button>
        </section>
      )}

      {/* My Tiffins */}
      <section data-testid="customer-updates-mytiffins-section">
        <SectionLabel>History</SectionLabel>
        <button
          type="button"
          onClick={() => navigate("/customer/history")}
          className="flex w-full items-center gap-3 rounded-2xl border border-border bg-surface p-4 text-left shadow-sm transition-all active:scale-[0.99]"
          data-testid="customer-updates-mytiffins-button"
        >
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Calendar size={20} strokeWidth={1.5} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-main">My Tiffins</span>
            <span className="block text-xs text-text-muted">Your delivery history & calendar</span>
          </span>
          <ChevronRight size={18} strokeWidth={1.5} className="flex-shrink-0 text-text-muted" />
        </button>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          data-testid="customer-updates-lightbox"
        >
          <button type="button" className="absolute right-4 top-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={22} strokeWidth={1.5} />
          </button>
          <img src={lightbox} alt="Update" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
        </div>
      )}

      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
